import './session-date-picker.scss';

import { LeftOutlined, RightOutlined } from '@ant-design/icons';
import { Button, Calendar, Col, Empty, Row } from 'antd';
import { CalendarMode } from 'antd/lib/calendar/generateCalendar';
import moment from 'moment';
import React, { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import SessionDateCell from './session-date-cell';
import LoadingSpinner from '../../components/loading-spinner/loading-spinner';
import IOpenDate, { OpenDateStatus } from '../../interfaces/IOpenDate';

import { toApiDateString } from '../../helpers/dateFormatting';
import { ITicketPriceRange } from '../../interfaces/ITicketPriceRange';
import { useSelector } from 'react-redux';
import { IApplicationState } from '../../store';
import PriceBucketLabels from '../../components/priceBucketLabels/PriceBucketLabels';
import { PurchaseTypes } from '../../store/PurchaseTypes';

export interface ISessionDatePickerProps {
    initalDate: string | undefined;
    selectedDate: string | undefined;
    availableDates: Map<string, IOpenDate> | undefined;
    monthHeaderOptions: ICalendarHeaderOption[];
    canPageBack: boolean;
    canPageForward: boolean;
    onPageMonths: (delta: number) => void;
    loading: boolean;
    extending: boolean;
    locale: string;
    onDateChange: (date: moment.Moment) => void;
}

export interface ICalendarHeaderOption {
    label: string;
    month: number;
    year: number;
}

const SessionDatePicker: React.FunctionComponent<ISessionDatePickerProps> = (props) => {
    const { t } = useTranslation();
    const emptyLogo = `/static/zl-black-new.png`;
    const {
        availableDates,
        onDateChange,
        locale,
        loading,
        extending,
        monthHeaderOptions,
        canPageBack,
        canPageForward,
        onPageMonths,
        initalDate,
        selectedDate,
    } = props;

    //Has the component done its initialization run yet y/n
    const [isInit, setIsInit] = useState(false);

    const [dateValue, setDateValue] = useState(moment()); //the date the rest of the app knows about
    const [visualDate, setvisualDate] = useState(moment()); //the date used to fake visual user selection

    const highlightPriceEnabled = useSelector((state: IApplicationState) => state.sites.bookingOptions?.usePriceTierHighlighting) ?? false;
    const selectedPurchaseType = useSelector((state: IApplicationState) => state.purchaseType.selectedPurchaseType);

    const priceBucketsEnabled = useMemo(() => {
        return highlightPriceEnabled && selectedPurchaseType === PurchaseTypes.Booking
    }, [highlightPriceEnabled, selectedPurchaseType]);

    // This use effect will help understand what is going on here via the console.
    // We had to split the date value and the visual value because we wanted users to be able to switch
    // which month was selected without the app fetching date date for the next month and auto scrolling away from the calendar.
    // Ant design controls its rendering based on the Calendar value so we had to tell the calendar a new date but not report a changed event.

    // useEffect(() => {
    //     console.log(`visually: ${visualDate.date()} / ${visualDate.month()} / ${visualDate.year()}`);
    //     console.log(`actually: ${dateValue.date()} / ${dateValue.month()} / ${dateValue.year()}`);
    // }, [dateValue, visualDate]);

    const ticketPriceRange: ITicketPriceRange = React.useMemo(() => {

        if (availableDates === undefined || availableDates.size === 0) {
            //console.log(`session-date-picker ticketPriceRange returning zero due to no available dates`);
            return { min: 0, max: 0, range20: 0, highlightThreshold: 0, rangeIsValid: false } as ITicketPriceRange;
        }

        // Array.from required: Map.values() returns an iterator; spread [...map.values()] needs --downlevelIteration (not enabled in this tsconfig)
        const allValidDates = Array.from(availableDates.values()).filter((x: IOpenDate) => x.minTicketPrice > 0 && x.maxTicketPrice > 0);

        if (allValidDates === undefined || allValidDates.length <= 0) {
            //console.log(`session-date-picker ticketPriceRange returning zero due to no valid dates`);
            return { min: 0, max: 0, range20: 0, highlightThreshold: 0, rangeIsValid: false } as ITicketPriceRange;
        }

        const prices = allValidDates.map((d) => d.minTicketPrice);
        const min = Math.min(...prices) ?? 0;
        const max = Math.max(...prices) ?? 0;

        const range20 = min === max ? 0 : (max - min) * 0.2;
        const highlightThreshold = min + range20;

        const isValid = min !== max && highlightThreshold > 0;

        const res = { min, max, range20, highlightThreshold, rangeIsValid: isValid } as ITicketPriceRange;
        //console.log(res);

        return res;
    }, [availableDates]);


    //run the first time init if required, or catch selectedDate being reset
    if (initalDate) {
        var newInitialMoment = moment(initalDate, 'YYYY-MM-DD');

        //run the first time init if the component is loading for the first time
        if (!isInit) {
            setIsInit(true);

            //set the dates to either the default 'initialDate', or the 'selectedDate' if it exists
            if (selectedDate) {
                newInitialMoment = moment(selectedDate);
            }

            //update both the data and the ui
            setDateValue(newInitialMoment);
            setvisualDate(newInitialMoment);
        } else if (!dateValue.isSame(newInitialMoment) && (!selectedDate || !dateValue.isSame(selectedDate))) {
            //otherwise if the visual date doesn't match either the initial date or the selected date reset it
            // to catch the case where the selected date is nulled out
            setDateValue(newInitialMoment);
            setvisualDate(newInitialMoment);
        }
    }

    const monthSelected = useCallback(
        (
            option: ICalendarHeaderOption,
            _onChange: (value: moment.Moment) => void,
            _onTypeChange: (type: CalendarMode) => void
        ) => {
            const { year, month } = option;
            const newValue = visualDate.clone().year(year).month(month);
            setvisualDate(newValue);
        },
        [visualDate]
    );

    // shift the month tabs by one month and show the month that has just come into view
    const pageMonths = useCallback(
        (delta: number) => {
            const edge = delta > 0 ? monthHeaderOptions[monthHeaderOptions.length - 1] : monthHeaderOptions[0];
            if (!edge) return;

            const revealed = moment({ year: edge.year, month: edge.month, day: 1 }).add(delta, 'months');
            setvisualDate(visualDate.clone().year(revealed.year()).month(revealed.month()));
            onPageMonths(delta);
        },
        [monthHeaderOptions, onPageMonths, visualDate]
    );

    const headerRenderer = (
        value: moment.Moment,
        type: any,
        onChange: (value: moment.Moment) => void,
        onTypeChange: (type: CalendarMode) => void
    ): React.ReactNode => {
        return (
            <section className="zl-calendar-header">
                <Button
                    type="ghost"
                    className="zl-month-nav"
                    icon={<LeftOutlined />}
                    disabled={!canPageBack}
                    onClick={() => pageMonths(-1)}
                />
                {monthHeaderOptions.map((option: ICalendarHeaderOption) => {
                    const isSelected = value.month() === option.month && value.year() === option.year;
                    return (
                        <Button
                            type="ghost"
                            className={`zl-month-button ${isSelected ? 'selected' : ''}`}
                            key={`${option.year}-${option.month}`}
                            data-month={option.month}
                            data-year={option.year}
                            onClick={(_event: React.MouseEvent<HTMLElement, MouseEvent>) => {
                                monthSelected(option, onChange, onTypeChange);
                            }}
                        >
                            {option.label}
                        </Button>
                    );
                })}
                <Button
                    type="ghost"
                    className="zl-month-nav"
                    icon={<RightOutlined />}
                    loading={extending}
                    disabled={!canPageForward || extending}
                    onClick={() => pageMonths(1)}
                />
            </section>
        );
    };

    const dateRenderer = useCallback(
        (current: moment.Moment) => {


            const availability = availableDates?.get(toApiDateString(current));
            const status = availability?.status;
            const disabled =
                availability === undefined || status === OpenDateStatus.Closed || status === OpenDateStatus.Full;
            const now = moment();
            const selected = dateValue;
            const visual = visualDate;

            const highlightCell : boolean = ticketPriceRange.rangeIsValid
                && availability?.minTicketPrice !== undefined
                && availability?.minTicketPrice > 0
                && availability?.minTicketPrice < ticketPriceRange.highlightThreshold

            return (
                <SessionDateCell
                    status={status}
                    today={current.isSame(now, 'date')}
                    past={current.isBefore(now, 'date')}
                    thisMonth={current.isSame(visual, 'month')}
                    selected={
                        selectedDate !== undefined &&
                        current.isSame(selected, 'date') &&
                        current.isSame(selected, 'month')
                    }
                    day={current.date()}
                    month={current.month()}
                    year={current.year()}
                    locale={locale}
                    disabled={disabled}
                    min={availability?.minTicketPrice}
                    max={availability?.maxTicketPrice}
                    highlightPrice={highlightCell}
                    priceRangeValid={priceBucketsEnabled && ticketPriceRange.rangeIsValid}
                />
            );
        },
        [availableDates, dateValue, locale, visualDate, ticketPriceRange, priceBucketsEnabled, selectedDate]
    );

    const disabledDate = useCallback(
        (currentDate: moment.Moment | undefined): boolean => {
            if (currentDate === undefined) return true; //no date??

            if (availableDates === undefined) return true; //no dates available

            const availability = availableDates.get(toApiDateString(currentDate));
            if (availability === undefined) return true;

            const status = availability.status;
            if (status === OpenDateStatus.Closed || status === OpenDateStatus.Full) return true;

            return false;
        },
        [availableDates]
    );

    const selectedDateChange = useCallback(
        (date?: moment.Moment | null) => {
            if (date && onDateChange) {
                setDateValue(date);
                setvisualDate(date);
                onDateChange(date);
            }
        },
        [onDateChange]
    );

    const NoAvailabilityState = (): React.ReactElement => {
        return (
            <Row justify="start" align="top">
                <Col span={24}>
                    <Empty
                        description={t('sessionDatePicker.labels.noDatesAvailable')}
                        image={emptyLogo}
                        imageStyle={{ opacity: 0.1, height: 50, margin: 20 }}
                    />
                </Col>
            </Row>
        );
    };

    const EmptyState = (): React.ReactElement => {
        return (
            <Row justify="start" align="top">
                <Col span={24}>
                    <Empty
                        description={t('sessionDatePicker.labels.selectDatePlayersFirst')}
                        image={emptyLogo}
                        imageStyle={{ opacity: 0.1, height: 50, margin: 20 }}
                    />
                </Col>
            </Row>
        );
    };

    if (loading) {
        return <LoadingSpinner />;
    }
    if (availableDates === undefined) {
        return <EmptyState />;
    }
    else if (availableDates.size === 0) {
        return <NoAvailabilityState />;
    }

    return (
        <>
            <Row justify="start" align="top" style={{ paddingBottom:"10px" }} translate="no">
                <Col>
                    <Calendar
                        className={`zl-calendar ${extending ? 'extending' : ''}`}
                        onSelect={selectedDateChange}
                        dateFullCellRender={dateRenderer}
                        disabledDate={disabledDate}
                        fullscreen={true}
                        value={visualDate}
                        mode="month"
                        headerRender={({ value, type, onChange, onTypeChange }): React.ReactNode => {
                            return headerRenderer(value, type, onChange, onTypeChange);
                        }}
                    />
                </Col>
            </Row>
            {priceBucketsEnabled && ticketPriceRange.rangeIsValid && <PriceBucketLabels />}
        </>
    );
};

export default SessionDatePicker;
